import { useState } from 'react';
import { ChevronDown, Download, FileSpreadsheet, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Scan, ScanAPI, ScanStatus } from '@/api';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from './ui/dropdown-menu';
import { UpgradeTooltip } from './UpgradeTooltip';

interface ScanReportActionsProps {
  scan: Scan;
  canExportScanReport: boolean;
}

type PendingDownload = 'ort' | 'report' | null;

/**
 * Scan report header "Download Report" dropdown (ORT files + Excel scan report).
 * Re-Scan / Archive / Delete stay in the header's ⋯ menu.
 */
export function ScanReportActions({ scan, canExportScanReport }: ScanReportActionsProps) {
  const [pending, setPending] = useState<PendingDownload>(null);
  const isCompleted = scan.scanstatus === ScanStatus.COMPLETED;

  const handleDownloadOrt = async () => {
    setPending('ort');
    try {
      await ScanAPI.downloadOrtReport(scan.id);
      toast.success('ORT report downloaded successfully');
    } catch (error) {
      toast.error('Failed to download ORT report. Please try again.');
    } finally {
      setPending(null);
    }
  };

  const handleExportScanReport = async () => {
    setPending('report');
    const loadingToast = toast.loading('Generating Scan report... This may take a few moments.');
    try {
      await ScanAPI.downloadSccReport(scan.id);
      toast.dismiss(loadingToast);
      toast.success('Scan report downloaded successfully');
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error('Failed to download Scan report. Please try again.');
    } finally {
      setPending(null);
    }
  };

  const triggerButton = (
    <Button variant="outline" size="sm" disabled={!isCompleted || pending !== null} aria-busy={pending !== null}>
      {pending ? (
        <>
          <Loader2 className="size-4 animate-spin" />
          Preparing…
        </>
      ) : (
        <>
          <Download className="size-4" />
          Download Report
          <ChevronDown className="size-4 opacity-60" />
        </>
      )}
    </Button>
  );

  if (!isCompleted) {
    return <UpgradeTooltip message="Available when the scan completes.">{triggerButton}</UpgradeTooltip>;
  }

  const exportItem = (
    <DropdownMenuItem
      onClick={canExportScanReport ? handleExportScanReport : undefined}
      disabled={!canExportScanReport}
      className="items-start"
    >
      <FileSpreadsheet className="mt-0.5" />
      <div className="flex flex-col">
        <span>Export Scan Report</span>
        <span className="text-xs text-muted-foreground">Excel report (.xlsx)</span>
      </div>
    </DropdownMenuItem>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[14rem]">
        <DropdownMenuItem onClick={handleDownloadOrt} className="items-start">
          <Download className="mt-0.5" />
          <div className="flex flex-col">
            <span>Download ORT Files</span>
            <span className="text-xs text-muted-foreground">ORT result files (.zip)</span>
          </div>
        </DropdownMenuItem>
        {canExportScanReport ? (
          exportItem
        ) : (
          <UpgradeTooltip side="left" message="The comprehensive Excel scan report is available on the Professional plan and above.">
            {exportItem}
          </UpgradeTooltip>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
